import React, { useState, useEffect, useRef } from 'react';
import App from './App';
import LoginScreen from './screens/LoginScreen';
import PaywallScreen from './screens/PaywallScreen';
import { Background } from './components/game/Background';
import { Mascot } from './components/game/Mascot';
import { api } from './lib/api';

function FullScreenLoader() {
  return (
    <Background>
      <div className="flex min-h-[100dvh] items-center justify-center">
        <div className="anim-bob">
          <Mascot mood="idle" size={130} />
        </div>
      </div>
    </Background>
  );
}

export default function AuthGate() {
  const [status, setStatus] = useState('loading'); // loading | login | paywall | app
  const [user, setUser] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [payNotice, setPayNotice] = useState(''); // '' | 'pending' | 'failure'
  const payRefRef = useRef(null);
  const payPidRef = useRef(null);
  const processed = useRef(false);

  const decide = (u) => {
    setUser(u);
    setStatus(u.paid === 'lifetime' ? 'app' : 'paywall');
  };

  const cleanPayQuery = () => {
    window.history.replaceState(null, '', window.location.pathname);
  };

  const bootstrap = async () => {
    const params = new URLSearchParams(window.location.search);
    const pay = params.get('pay');
    const ref = params.get('ref');
    // Mercado Pago devolve o id do pagamento na URL de retorno.
    const paymentId = params.get('payment_id') || params.get('collection_id');
    payRefRef.current = ref;
    payPidRef.current = paymentId;
    try {
      const hash = window.location.hash || '';
      if (hash.includes('session_id=')) {
        const sid = new URLSearchParams(hash.replace(/^#/, '')).get('session_id');
        if (sid) {
          await api.exchangeSession(sid);
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      }

      let u = await api.me();

      // Ao voltar do pagamento, confirma na hora usando o payment_id.
      if (ref && pay !== 'failure' && u.paid !== 'lifetime') {
        setConfirming(true);
        for (let i = 0; i < 8; i++) {
          try {
            const st = await api.paymentStatus(ref, payPidRef.current);
            if (st.paid) break;
          } catch {
            /* segue tentando */
          }
          await new Promise((r) => setTimeout(r, 2000));
        }
        u = await api.me();
        setConfirming(false);
      }

      // Rede de segurança (Pix / webhook perdido / sem redirecionamento):
      // reconcilia os pedidos recentes do usuário direto no Mercado Pago.
      if (u.paid !== 'lifetime') {
        try {
          const r = await api.reconcile();
          if (r.paid) u = await api.me();
        } catch {
          /* segue */
        }
      }

      if (pay) cleanPayQuery();

      if (u.paid !== 'lifetime') {
        if (pay === 'failure') setPayNotice('failure');
        else if (pay === 'pending' || pay === 'success') setPayNotice('pending');
      }
      decide(u);
    } catch {
      if (pay) cleanPayQuery();
      setStatus('login');
    }
  };

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshUser = async () => {
    try {
      setConfirming(true);
      if (payRefRef.current) {
        try {
          await api.paymentStatus(payRefRef.current, payPidRef.current);
        } catch {
          /* ignora e segue para o /me */
        }
      }
      try {
        await api.reconcile();
      } catch {
        /* ignora e segue para o /me */
      }
      const u = await api.me();
      setConfirming(false);
      if (u.paid === 'lifetime') setPayNotice('');
      decide(u);
    } catch {
      setConfirming(false);
      setStatus('login');
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    setUser(null);
    setStatus('login');
  };

  if (status === 'loading') return <FullScreenLoader />;
  if (status === 'login') return <LoginScreen />;
  if (status === 'paywall')
    return (
      <PaywallScreen
        user={user}
        confirming={confirming}
        notice={payNotice}
        onRecheck={refreshUser}
        onLogout={logout}
      />
    );
  return <App user={user} onLogout={logout} />;
}
